import { Box, Flex, FlexProps, Text } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Channel,
  FS_ENABLE_META_ARRIVAL_DAY_KEYWORD_PI,
  FS_SILENT_SUBSTITUTION,
  HeaderInformationQuery,
  HIHotelAvailabilityResponse,
  HIVisualDisplayContext,
  PageName,
  SearchRoomType,
  SITE_LEISURE,
  FT_PI_PROMO_CODE_LANDING_PAGE,
  PROMO_CODE_LANDING_PAGE_COOKIE,
  PROMO_CODE_LANDING_PAGE_SESSION,
  getStaticContent,
  FT_PI_BB_CCUI_BARRIER_FREE_LABEL,
  CountryCode,
} from '@whitbread-eos/api';
import {
  AnnouncementNotification,
  HotelBadges,
  HotelBreadcrumb,
  HotelContactInformation,
  HotelDescriptionInformation,
  HotelFacilities,
  HotelFaq,
  HotelGallery,
  HotelHeadline,
  HotelLocation,
  HotelParkingInformation,
  HotelRestaurant,
  HotelRooms,
  HotelTitle,
  HubZipNotice,
  OpeningSoonNotification,
  SEARCH_REFERRER_INITIAL_VALUE,
  SEO as Seo,
  SoldOutNotification,
  TripAdvisorReview,
} from '@whitbread-eos/molecules';
import {
  formatDataTestId,
  getCookie,
  getMappedRooms,
  getRatePlanCode,
  getTodayTomorrowDate,
  isDateValid,
  isFaqValidForRender,
  isHotelOpeningSoon as isHotelOpeningSoonFunction,
  swapKeysAndValues,
  updateHotelDisplayPageAnalytics,
  useCustomLocale,
  useDiscountRateInfoHotelAvailability,
  useFeatureSwitch,
  useFeatureToggle,
  useForDiscountedRateMicroSite,
  useHotelAvailability,
  useLocalStorage,
  useQueryRequest,
  useStaticHotelInformation,
} from '@whitbread-eos/utils';
import add from 'date-fns/add';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import dynamic from 'next/dynamic';
import DefaultErrorPage from 'next/error';
import { NextRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

interface QueryParams {
  [key: string]: string;
}

const labelsConstants = {
  AVAILABLE_HOTEL: 'available',
  UNAVAILABLE_HOTEL: 'not available',
  UNCHECKED_HOTEL: 'not checked',
};

interface Props {
  queryClient: QueryClient;
  router: NextRouter;
  visualDisplayContext: HIVisualDisplayContext;
}

const Location = dynamic(
  async () => {
    const { Location } = await import('@whitbread-eos/organisms');
    return { default: Location };
  },
  {
    loading: () => <Text> </Text>,
    ssr: false,
  }
);

const RateSelector = dynamic(
  async () => {
    const { RateSelector } = await import('@whitbread-eos/organisms');
    return { default: RateSelector };
  },
  {
    loading: () => <Text> </Text>,
    ssr: false,
  }
);

const SearchContainer = dynamic(
  async () => {
    const { PISearchContainer } = await import('@whitbread-eos/organisms');
    return { default: PISearchContainer };
  },
  {
    ssr: false,
  }
);

export default function HotelDetailsPagePI({
  queryClient,
  router,
  visualDisplayContext,
}: Readonly<Props>) {
  const [isParentAnalytics, setIsParentAnalytics] = useState(false);
  const { isLessThanXs, isLessThanSm, isLessThanMd, isLessThanLg } = visualDisplayContext;
  const { searchLocation, NIGHTS, ROOMS } = router.query;
  let { ARRdd, ARRmm, ARRyyyy } = router.query;

  // use helm (server side) feature switch to ensure toggle ready before page load
  const isMetaArrivalDayKeywordFlagEnabled = useFeatureSwitch({
    featureSwitchKey: FS_ENABLE_META_ARRIVAL_DAY_KEYWORD_PI,
    fallbackValue: false,
  });

  // for meta query url - with today or tomorrow set for ARRdd, as only date
  const updatedArrivalDate = getTodayTomorrowDate(ARRdd, ARRmm, ARRyyyy);

  if (isMetaArrivalDayKeywordFlagEnabled) {
    ARRdd = updatedArrivalDate.ARRdd;
    ARRmm = updatedArrivalDate.ARRmm;
    ARRyyyy = updatedArrivalDate.ARRyyyy;
  }

  const { AVAILABLE_HOTEL, UNAVAILABLE_HOTEL, UNCHECKED_HOTEL } = labelsConstants;
  const [, setSearchReferrer] = useLocalStorage('SearchReferrer', SEARCH_REFERRER_INITIAL_VALUE);
  const baseDataTestId = 'HotelDetailsPIPage';
  const { language, country } = useCustomLocale();
  const isSilentFeatureFlagEnabled = useFeatureSwitch({
    featureSwitchKey: FS_SILENT_SUBSTITUTION,
  });
  const isDiscountedRateEnabled = useForDiscountedRateMicroSite();

  const defaultRooms: SearchRoomType[] = [];
  for (let idx = 0; idx < Number(ROOMS); idx++) {
    const room: SearchRoomType = {
      adults: Number(router.query[`ADULT${idx + 1}`]),
      children: Number(router.query[`CHILD${idx + 1}`]),
      shouldIncludeCot: !!Number(router.query[`COT${idx + 1}`]),
      roomType: router.query[`INTTYP${idx + 1}`] as string,
    };
    defaultRooms.push(room);
  }

  const {
    [FT_PI_PROMO_CODE_LANDING_PAGE]: isPromoCodeLandingPageEnabled,
    [FT_PI_BB_CCUI_BARRIER_FREE_LABEL]: isBarrierFreeLabelEnabledFT,
  } = useFeatureToggle();
  const isBarrierFreeLabelEnabled =
    language === CountryCode.DE ? isBarrierFreeLabelEnabledFT : false;

  const getLandingPagePromotionCode = (isPromoCodeLandingPageEnabled: boolean) => {
    if (!isPromoCodeLandingPageEnabled) {
      return '';
    }

    const landingPagePromoCdeCookie = getCookie(PROMO_CODE_LANDING_PAGE_COOKIE);
    if (typeof window === 'undefined') {
      return landingPagePromoCdeCookie;
    }
    if (sessionStorage.getItem(PROMO_CODE_LANDING_PAGE_SESSION)) {
      return landingPagePromoCdeCookie;
    } else {
      return '';
    }
  };

  const [landingPromoCode, setLandingPromoCode] = useState<string>(() =>
    getLandingPagePromotionCode(isPromoCodeLandingPageEnabled ?? false)
  );

  useEffect(() => {
    const activeLandingPagePromoCode = getLandingPagePromotionCode(
      isPromoCodeLandingPageEnabled ?? false
    );
    setLandingPromoCode(activeLandingPagePromoCode);
  }, [isPromoCodeLandingPageEnabled]);

  useEffect(() => {
    const cleanupReferrer = () => {
      setSearchReferrer(SEARCH_REFERRER_INITIAL_VALUE);
    };
    return () => cleanupReferrer();
  }, [setSearchReferrer]);

  useEffect(() => {
    window.localStorage.setItem('HDPPath', location.pathname + location.search);
    localStorage.removeItem('3cpVisited');
  }, []);

  // NOTE: get all data here that you need to pass to the molecules via props
  const {
    hotelId,
    name: hotelName,
    brand: hotelBrand,
    title: hotelTitle,
    breadcrumb: breadcrumbs,
    tripAdvisorReviews,
    headline,
    faq,
    hotelOpeningDate,
    hotelFacilities,
    isLoading,
    isError,
    error,
    coordinates,
    hotelDescription,
    address,
    links,
    galleryImages,
  } = useStaticHotelInformation();

  const { arrival, departure, numberOfUnits, rooms, numberOfNights, empOfferCodes, corpId } =
    useMemo(getMultiSearchParams, [router?.query]);

  let hotelAvailabilityResponse: HIHotelAvailabilityResponse | any;
  let dataHotelAvailability: HIHotelAvailabilityResponse | any;

  const discountRateAndCorpIdDisabled = !isDiscountedRateEnabled && !corpId;
  const arrivaldate = discountRateAndCorpIdDisabled ? arrival : undefined;
  const discountRateAndCorpIdEnabled = !!isDiscountedRateEnabled && !!corpId;

  const {
    isLoading: isLoadingHotelAvailability,
    isError: isErrorHotelAvailability,
    data: dataHotelAvailabilityPI,
    error: errorHotelAvailability,
  } = useHotelAvailability(
    hotelId,
    hotelBrand,
    arrivaldate,
    departure,
    rooms,
    'PI',
    empOfferCodes,
    '',
    landingPromoCode
  );

  const query = getStaticContent(isBarrierFreeLabelEnabled);
  const { data: dataHeaderInformation }: HeaderInformationQuery = useQueryRequest(
    ['GetStaticContent', language, country],
    query,
    {
      country,
      language,
      site: SITE_LEISURE,
      businessBooker: false,
    }
  );

  const discountRateInfo = useDiscountRateInfoHotelAvailability({
    hotelId,
    hotelBrand,
    arrival: arrival as string,
    departure: departure as string,
    rooms,
    offers: dataHeaderInformation?.headerInformation?.content?.global?.offers,
  });

  if (discountRateAndCorpIdEnabled) {
    dataHotelAvailability = discountRateInfo?.dataHotelAvailability;
    hotelAvailabilityResponse = discountRateInfo?.hotelAvailabilityResponse;
  } else {
    dataHotelAvailability = dataHotelAvailabilityPI;
    hotelAvailabilityResponse = {
      isLoadingHotelAvailability,
      isErrorHotelAvailability,
      dataHotelAvailability,
      errorHotelAvailability,
    };
  }

  const srcHubLogo = dataHeaderInformation?.headerInformation?.content?.global?.brand?.hubHdpLogo;
  const thirdParties =
    dataHeaderInformation?.headerInformation?.content?.global?.thirdParties ?? '';

  const roomCodes = dataHeaderInformation?.headerInformation?.config?.roomCodes || {};
  const {
    double = '',
    accessible = '',
    family = '',
    single = '',
    twin = '',
  } = dataHeaderInformation?.headerInformation?.content?.global || {};
  const globalTranslationForRooms = { double, accessible, family, single, twin };

  const mappedRoomLabels =
    (isSilentFeatureFlagEnabled &&
      swapKeysAndValues(getMappedRooms(roomCodes, globalTranslationForRooms))) ||
    {};

  const isHotelOpeningSoon = useMemo(
    () => isHotelOpeningSoonFunction(hotelOpeningDate ?? '', arrival),
    [hotelOpeningDate, arrival]
  );

  const isHotelAvailable =
    dataHotelAvailability?.hotelAvailability?.available &&
    dataHotelAvailability?.hotelAvailability?.roomRates.length > 0;

  const isDisplayRates = !isHotelOpeningSoon && isHotelAvailable;

  // HDP Analytics
  useEffect(() => {
    let hotelAvailability;

    if (!ROOMS) {
      hotelAvailability = UNCHECKED_HOTEL;
    } else if (!isHotelOpeningSoon && isHotelAvailable) {
      hotelAvailability = AVAILABLE_HOTEL;
    } else {
      hotelAvailability = UNAVAILABLE_HOTEL;
    }

    if (hotelName) {
      updateHotelDisplayPageAnalytics({
        multiSearchParams: router.query as QueryParams,
        hotelName,
        hotelId,
        hotelLabel: hotelBrand,
        get hotelAvailability() {
          return hotelAvailability;
        },
        hotelFacilityIcons: hotelFacilities.map((facility) => facility.icon ?? ''),
        isMetaArrivalDayKeywordFlagEnabled,
        dataHotelAvailability: dataHotelAvailabilityPI,
      });

      setIsParentAnalytics(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, ROOMS, hotelName, hotelBrand, hotelId, dataHotelAvailability]);

  if (isLoading) {
    return <Text data-testid="HotelDetailsPIPage-Wrapper-loading"> </Text>;
  }

  if (isError) {
    return <Text>{(error as Error).message}</Text>;
  }

  if (!hotelId) {
    return <DefaultErrorPage statusCode={404} />;
  }

  if (discountRateAndCorpIdEnabled && discountRateInfo.isDiscountRateLoading) {
    return <Text data-testid="HotelDetailsPIPage-Wrapper-loading"> </Text>;
  }

  const targetRatePlanCode = getRatePlanCode(
    dataHeaderInformation?.headerInformation?.content?.global?.offers as any,
    corpId as string
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Box data-testid={formatDataTestId(baseDataTestId, 'Wrapper')}>
        <Seo
          page={PageName.HDP}
          displayMeta
          {...{
            hotelId,
            tripAdvisorReviews,
            breadcrumbs,
          }}
          hotel={{
            coordinates,
            hotelDescription,
            address,
            links,
            hotelId,
            name: hotelName,
            tripAdvisorReviews,
            galleryImages,
          }}
        />
        <SearchContainer
          queryClient={queryClient}
          searchLocation={searchLocation?.toString()}
          defaultLocation={hotelName}
          defaultRooms={defaultRooms}
          ARRdd={Number(ARRdd)}
          ARRmm={Number(ARRmm)}
          ARRyyyy={Number(ARRyyyy)}
          NIGHTS={Number(NIGHTS)}
          ROOMS={Number(ROOMS)}
          CORPID={corpId?.toString()}
          variant="pi"
        />
        <AnnouncementNotification queryClient={queryClient} />
        <OpeningSoonNotification
          {...{ queryClient, hotelAvailabilityResponse, isHotelOpeningSoon }}
        />
        {ARRyyyy && ARRmm && ARRdd && (
          <SoldOutNotification
            {...{ queryClient, hotelAvailabilityResponse, isHotelOpeningSoon }}
          />
        )}
        <HubZipNotice queryClient={queryClient} srcHubLogo={srcHubLogo} />
        <HotelBreadcrumb queryClient={queryClient} />
        <Flex my="xl" direction={{ base: 'column-reverse', lg: 'initial' }}>
          <Box flex={3.5} data-testid="hdp_hotelInformationTopSection" mr="md">
            <Box mb="sm">
              <HotelTitle name={hotelTitle} />
            </Box>
            <TripAdvisorReview queryClient={queryClient} isTopSection={true} />
            <Box mb="sm">
              <HotelLocation />
            </Box>
            <Box my="md">
              <HotelBadges
                queryClient={queryClient}
                hubBadge={
                  dataHeaderInformation?.headerInformation?.content?.global?.brand?.hubBadge ?? ''
                }
              />
              <HotelHeadline headline={headline} />
            </Box>
            {isLessThanSm && (
              <Box borderBottom="1px solid var(--chakra-colors-lightGrey4)" mt="md" />
            )}
            <HotelFacilities {...{ queryClient, isLessThanSm }} />
          </Box>
          <Box flex={4}>
            <HotelGallery
              thumbnailSectionHeight="18.75rem"
              {...{ queryClient, isLessThanMd, isLessThanLg }}
            />
          </Box>
        </Flex>
        <Box
          data-testid="hdp_hotelInformationRatesSpacer"
          borderBottom="1px solid var(--chakra-colors-lightGrey4)"
          mb="3xl"
        />
        {!!numberOfNights && !!numberOfUnits && (
          <RateSelector
            variant="PI"
            channel={Channel.Pi}
            {...{
              queryClient,
              hotelAvailabilityResponse,
              isParentAnalytics,
              arrival,
              departure,
              numberOfNights,
              numberOfUnits,
              isHotelOpeningSoon,
              isLessThanSm,
              isLessThanMd,
              isLessThanLg,
              thirdParties,
              mappedRoomLabels,
              isSilentFeatureFlagEnabled,
              targetRatePlanCode: targetRatePlanCode ?? '',
              globalTranslationForRooms,
            }}
          />
        )}
        <Location queryClient={queryClient} />
        <HotelParkingInformation queryClient={queryClient} />
        <HotelRooms
          isPremierInn={true}
          {...{ queryClient, isLessThanSm, isLessThanMd, isLessThanLg, isDisplayRates }}
        />
        <HotelRestaurant {...{ queryClient, isLessThanXs }} />
        <span id="tripadvisor-reivew-section-bottom"></span>
        <TripAdvisorReview queryClient={queryClient} isTopSection={false} />
        <Flex {...hdpDescriptionFaq}>
          <Box flex={'1'} borderBottom={'none'}>
            <HotelDescriptionInformation queryClient={queryClient} />
          </Box>
          {isFaqValidForRender(faq, true) && (
            <Box flex={'1'} data-testid="hdp-faq-pi">
              <HotelFaq queryClient={queryClient} />
            </Box>
          )}
        </Flex>
        <HotelContactInformation queryClient={queryClient} />
      </Box>
    </QueryClientProvider>
  );

  function getMultiSearchParams() {
    if (!router?.query) return {};
    const { NIGHTS, ROOMS, CELLCODES, CORPID } = router.query;
    const cellCodes = CELLCODES as string;
    const empOfferCodes = (cellCodes?.split(',') as string[] | []) || [];

    // Set arrival and departure as undefined if the page is accessed from an external source without params
    // In this case both server-side request and page request will not be consumed
    if (
      !ARRdd &&
      !ARRmm &&
      !ARRyyyy &&
      !isDateValid(Number(ARRdd), Number(ARRmm), Number(ARRyyyy))
    ) {
      return {
        arrival: undefined,
        departure: undefined,
        rooms: [],
      };
    }

    //Set default search parameters if one the required params is missing
    if (
      !ARRdd ||
      !ARRmm ||
      !ARRyyyy ||
      !NIGHTS ||
      !ROOMS ||
      !isDateValid(Number(ARRdd), Number(ARRmm), Number(ARRyyyy))
    ) {
      return {
        arrival: format(endOfDay(new Date()), 'yyyy-MM-dd'),
        departure: format(endOfDay(add(new Date(), { days: Number(NIGHTS) || 1 })), 'yyyy-MM-dd'),
        numberOfNights: Number(NIGHTS) || 1,
        rooms: [
          {
            adultsNumber: 1,
            childrenNumber: 0,
            roomType: 'DB',
            cotRequired: false,
          },
        ],
        corpId: CORPID,
      };
    }

    const arrival = new Date(+ARRyyyy, +ARRmm - 1, +ARRdd);
    const departure = add(arrival, { days: +NIGHTS });
    const rooms = [];
    for (let idx = 0; idx < +ROOMS; idx++) {
      rooms.push({
        adultsNumber: +router.query[`ADULT${idx + 1}`]!,
        childrenNumber: +router.query[`CHILD${idx + 1}`]!,
        roomType: router.query[`INTTYP${idx + 1}`],
        cotRequired: router.query[`COT${idx + 1}`] === '1',
      });
    }

    return {
      arrival: format(arrival, 'yyyy-MM-dd'),
      departure: format(departure, 'yyyy-MM-dd'),
      numberOfUnits: +ROOMS,
      numberOfNights: +NIGHTS,
      rooms,
      empOfferCodes,
      corpId: CORPID,
    };
  }
}

const hdpDescriptionFaq = {
  borderBottom: '1px solid var(--chakra-colors-lightGrey1)',
  marginTop: '2px',
  marginBottom: '2px',
  gap: '20px',
  direction: { base: 'column', lg: 'initial' },
} as FlexProps;
